import { useContext, useCallback, useMemo } from "react";
import { ToastContext, ToastLink } from "../contexts/ToastContext";

interface AxiosLikeError {
  response?: {
    data?: {
      message?: string | string[];
    };
  };
  message?: string;
}

export const useToast = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used within ToastProvider");
  const { push } = ctx;

  const error = useCallback(
    (err: unknown, fallback = "Something went wrong.") => {
      // Most screens call `toast.error('Could not load …')` with the message
      // itself. A string has no `.response` or `.message`, so it used to fall
      // through to the fallback — every one of those read "Something went
      // wrong."
      if (typeof err === "string") {
        push("error", err);
        return;
      }
      const e = err as AxiosLikeError;
      const msg = e?.response?.data?.message;
      let message = fallback;
      if (Array.isArray(msg)) message = msg.join(", ");
      else if (typeof msg === "string") message = msg;
      else if (typeof e?.message === "string" && !e?.response)
        message = e.message;
      push("error", message);
    },
    [push],
  );

  return useMemo(
    () => ({
      success: (msg: string, link?: ToastLink) => push("success", msg, link),
      error,
      info: (msg: string, link?: ToastLink) => push("info", msg, link),
      warning: (msg: string, link?: ToastLink) => push("warning", msg, link),
    }),
    [push, error],
  );
};
